import universalFormatter, { resolveEcgType } from "./universalFormatter"
import { extractMedianBeatWindowsByType } from "./fiducialIntervals"
import { buildMedianBeatsField, buildFiducialPointLegend } from "./medianBeatSerialization"

// Headless "refresh all recordings in a task" flow. Iterates every recording,
// rebuilds the median-beat waveforms (v3 field) from the saved beats +
// window settings, and PUTs the recording back. Used by the TaskList
// download flow to backfill waveforms into older recordings so the resulting
// ZIP has them.
//
// Runs with bounded concurrency and aborts on first failure — the caller
// decides whether to proceed with the ZIP download in the partial-refresh
// state.
//
// No fiducial-point staleness check is performed here. The interactive save
// path in RightPanel is where staleness matters: a user can place points and
// then edit beats/window/inversion before saving, and the annotator prompts
// them to drop stale points. This flow only reads the *last saved* state and
// recomputes waveforms from the same beats/window/inversion the saved points
// were placed against, so the recomputed waveform is by construction the one
// those offsets refer to. Do not add a signature check here without also
// reconsidering whether the interactive check can be relaxed.

const METHODS = { GET: "get", PUT: "put" }

// Extract fiducial-points-by-type from either the v3 `medianBeats` blob or
// the legacy `medianBeatFiducialPoints` blob. Mirrors the older reducer path
// so a headless refresh preserves whatever the user last placed.
const extractFiducialPointsByType = (data) => {
    const annotations = data?.data?.annotations || {}
    const v3 = annotations.medianBeats
    if (v3 && v3.byBeatType && typeof v3.byBeatType === "object") {
        const out = {}
        for (const [slug, entry] of Object.entries(v3.byBeatType)) {
            const pts = entry?.fiducialPoints?.pointLabels
            if (Array.isArray(pts) && pts.length > 0) out[slug] = pts
        }
        return out
    }
    const v2 = annotations.medianBeatFiducialPoints
    if (v2 && !Array.isArray(v2) && v2.byBeatType) {
        const out = {}
        for (const [slug, entry] of Object.entries(v2.byBeatType)) {
            const pts = entry?.pointLabels
            if (Array.isArray(pts) && pts.length > 0) out[slug] = pts
        }
        return out
    }
    if (v2 && Array.isArray(v2.pointLabels) && v2.pointLabels.length > 0) {
        return { n: v2.pointLabels }
    }
    return {}
}

// Read the inversion flag from the saved `global` annotations. Only meaningful
// for non-12-lead recordings (12-lead hides the toggle and never persists it).
const extractInverted = (data) => {
    const globals = data?.data?.annotations?.global || []
    for (const g of globals) {
        if (g && typeof g === "object" && "isInverted" in g) return !!g.isInverted
    }
    return false
}

// Build a fake annotator-state snapshot that mirrors what the Annotator
// Redux slice would hold after loading this recording. Used only by
// `buildMedianBeatsField` and the shared compute helpers — nothing here
// touches Redux.
const buildHeadlessAnnotatorState = (recordingData) => {
    const type = resolveEcgType(recordingData?.data, recordingData?.data?.type)
    const uniEcg = universalFormatter(recordingData.data, type || "turk")
    if (uniEcg.error) {
        throw new Error(`universalFormatter failed: ${uniEcg.error}`)
    }
    const leadCount = Object.keys(uniEcg.filtered?.samples || {}).length
    const inverted = leadCount !== 12 ? extractInverted(recordingData) : false
    const medianBeatWindowByType = leadCount !== 12
        ? extractMedianBeatWindowsByType(
            recordingData?.data?.annotations?.medianBeats
                ?? recordingData?.data?.annotations?.medianBeatFiducialPoints
        )
        : {}
    return {
        rawData: recordingData,
        leadCount,
        original: uniEcg.original,
        filtered: uniEcg.filtered,
        annotations: {
            ...uniEcg.annotations,
            medianBeatFiducialPointsByType: extractFiducialPointsByType(recordingData),
        },
        settings: {
            inverted,
            medianBeatWindowByType,
            medianBeatBeatType: "n",
        },
    }
}

// Apply the v3 median-beats field to a recording payload in-place (well, to a
// cloned copy) and stamp an audit entry. Returns the cloned payload ready to
// PUT. Mirrors the write side of `performSave` in the two RightPanel files.
const buildRefreshedRecordingPayload = (recordingData, snapshot, userEmail) => {
    const payload = JSON.parse(JSON.stringify(recordingData))
    if (!Array.isArray(payload.data.annotator)) payload.data.annotator = []
    payload.data.annotator.push({
        time: new Date().toISOString(),
        person: userEmail,
    })
    if (!payload.data.legend) payload.data.legend = {}
    payload.data.legend.medianBeatFiducialPoints = buildFiducialPointLegend()
    payload.data.annotations.medianBeats = buildMedianBeatsField(snapshot, {})
    delete payload.data.annotations.medianBeatFiducialPoints
    return payload
}

// Backend DB pool is capped at 15 connections (SetMaxOpenConns) and shared
// with other endpoints, so we stay at 10 to leave headroom for concurrent
// traffic. Raising this above 15 gains nothing — requests just queue on the
// server-side semaphore.
const REFRESH_CONCURRENCY = 10

// Public: refresh median-beat waveforms for every recording in a task.
//
// Runs up to REFRESH_CONCURRENCY recordings in flight at once. Aborts on first
// PUT/GET/compute failure — returns { ok: false, ... } with the failing
// recording id. Successful path returns { ok: true, refreshed }.
//
// `onProgress({ completed, total, recordingId })` fires after each successful
// PUT so callers can update a progress indicator. `completed` is the count of
// successful PUTs so far (not an input index — order is non-deterministic).
export const refreshTaskWaveforms = async (
    axiosPrivate,
    taskId,
    userEmail,
    { onProgress } = {},
) => {
    let recordings
    try {
        const res = await axiosPrivate[METHODS.GET](`recordings?task_id=${taskId}`)
        recordings = res.data || []
    } catch (error) {
        return { ok: false, phase: "list", error, completed: 0, total: 0 }
    }
    const total = recordings.length
    if (total === 0) return { ok: true, refreshed: 0 }

    let nextIndex = 0
    let completed = 0
    let failure = null

    const processOne = async (rec) => {
        const recordingId = rec?.id
        const { data: recordingData } = await axiosPrivate[METHODS.GET](
            `recording?recording_id=${recordingId}`
        )
        const snapshot = buildHeadlessAnnotatorState(recordingData)
        const payload = buildRefreshedRecordingPayload(recordingData, snapshot, userEmail)
        await axiosPrivate[METHODS.PUT]("recording", payload)
        return recordingId
    }

    const worker = async () => {
        while (true) {
            if (failure) return
            const i = nextIndex++
            if (i >= recordings.length) return
            const rec = recordings[i]
            try {
                const recordingId = await processOne(rec)
                if (failure) return
                completed += 1
                if (onProgress) onProgress({ completed, total, recordingId })
            } catch (error) {
                if (!failure) {
                    failure = { recordingId: rec?.id, error }
                }
                return
            }
        }
    }

    const workerCount = Math.min(REFRESH_CONCURRENCY, recordings.length)
    await Promise.all(Array.from({ length: workerCount }, () => worker()))

    if (failure) {
        return {
            ok: false,
            phase: "recording",
            error: failure.error,
            recordingId: failure.recordingId,
            completed,
            total,
        }
    }
    return { ok: true, refreshed: total }
}
